import { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { PageHeader, Panel, StatusPill, EmptyState } from "../../components/Bits";
import { Modal } from "./Students";
import { api } from "../../lib/api";
import { toast } from "sonner";

const CAT_TINT = {
  fan: "bg-[#FBEED7] text-[#8C6D31]",
  light: "bg-[#FBEED7] text-[#8C6D31]",
  cooler: "bg-[#D6E6EB] text-[#3D5C68]",
  water_cooler: "bg-[#D6E6EB] text-[#3D5C68]",
  plumbing: "bg-[#D6E6EB] text-[#3D5C68]",
  furniture: "bg-[#EAE5DE] text-[#5A5652]",
  other: "bg-[#F4F1EA] text-[#75716E]",
};

export default function AdminTickets() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [updating, setUpdating] = useState({ status: "open", admin_note: "" });

  async function load() { const { data } = await api.get("/tickets"); setRows(data); }
  useEffect(() => { load(); }, []);

  function openTicket(t) { setActive(t); setUpdating({ status: t.status, admin_note: t.admin_note || "" }); }

  async function save(e) {
    e.preventDefault();
    await api.put(`/admin/tickets/${active.id}`, updating);
    toast.success("Ticket updated");
    setActive(null); load();
  }

  const filtered = (rows || []).filter((t) => filter === "all" ? true : t.status === filter);

  return (
    <AppShell>
      <PageHeader overline="Operations" title="Maintenance tickets" subtitle="Track and resolve issues raised by residents." />
      <Panel
        title={`${filtered.length} ${filtered.length === 1 ? "ticket" : "tickets"}`}
        action={
          <div className="flex gap-1 bg-[#F4F1EA] rounded-md p-1 text-sm">
            {["all", "open", "in_progress", "resolved", "closed"].map((k) => (
              <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1 rounded-md capitalize ${filter === k ? "bg-white text-deep shadow-sm" : "text-warm"}`} data-testid={`ticket-filter-${k}`}>
                {k.replace("_", " ")}
              </button>
            ))}
          </div>
        }
      >
        {rows === null ? <div className="text-sm text-warm">Loading…</div>
          : filtered.length === 0 ? <EmptyState title="No tickets" hint="Quiet times! Nothing's broken right now." />
            : (
              <ul className="divide-y divide-[#EAE5DE]">
                {filtered.map((t) => (
                  <li key={t.id} className="py-4 flex items-start justify-between gap-4 cursor-pointer hover:bg-[#FDFBF7] -mx-2 px-2 rounded-md" onClick={() => openTicket(t)} data-testid={`ticket-row-${t.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${CAT_TINT[t.category] || CAT_TINT.other}`}>{t.category.replace("_", " ")}</span>
                        <StatusPill status={t.status} />
                      </div>
                      <div className="font-medium text-deep mt-2">{t.title}</div>
                      <div className="text-sm text-warm line-clamp-1">{t.description}</div>
                      <div className="text-xs text-warm mt-1">{t.student_name} · Room {t.room_number || "—"} · {new Date(t.created_at).toLocaleString()}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
      </Panel>

      {active && (
        <Modal title={active.title} onClose={() => setActive(null)} wide>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3 text-sm">
              <div><div className="text-xs uppercase tracking-wider text-warm">Resident</div><div className="text-deep font-medium">{active.student_name}</div></div>
              <div><div className="text-xs uppercase tracking-wider text-warm">Room</div><div>{active.room_number || "—"}</div></div>
              <div><div className="text-xs uppercase tracking-wider text-warm">Category</div><div className="capitalize">{active.category.replace("_", " ")}</div></div>
              <div><div className="text-xs uppercase tracking-wider text-warm">Description</div><div className="whitespace-pre-wrap">{active.description}</div></div>
              <div><div className="text-xs uppercase tracking-wider text-warm">Raised</div><div>{new Date(active.created_at).toLocaleString()}</div></div>
            </div>
            <form onSubmit={save} className="space-y-3">
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-deep font-medium mb-1.5 block">Status</span>
                <select value={updating.status} onChange={(e) => setUpdating({ ...updating, status: e.target.value })} className={input}>
                  <option value="open">Open</option><option value="in_progress">In progress</option><option value="resolved">Resolved</option><option value="closed">Closed</option>
                </select>
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-deep font-medium mb-1.5 block">Admin note</span>
                <textarea rows={4} value={updating.admin_note} onChange={(e) => setUpdating({ ...updating, admin_note: e.target.value })} className={input} />
              </label>
              <button type="submit" data-testid="update-ticket-btn" className="bg-terracotta text-white px-6 py-2 rounded-md text-sm">Update ticket</button>
            </form>
          </div>
        </Modal>
      )}
    </AppShell>
  );
}

const input = "w-full px-3 py-2 rounded-md border border-warm bg-white focus:outline-none focus:ring-2 focus:ring-[#E8C2B8] focus:border-[#C86F58] text-sm";
